// Строки интерфейса по ключам (E01-T11, ADR-0026). Ключи — ID нарратива (`UI-…`, `ERR-…`, `TUT-…`), а где
// пары ещё нет — черновые `CL-…` (тексты на вычитку, E20-T25). Формат сообщений — ICU-подмножество:
// `{name}` и `{n, plural, one {…} few {…} many {…} other {…}}` с `#` = n.
// Пакет-перекрытие приходит без релиза клиента: bootstrap.clientConfig.values.strings = {KEY: текст}.
// Он кэшируется в localStorage, поэтому и строки, вычисленные при загрузке модулей, подхватывают его со
// следующего запуска; строки, вычисленные при отрисовке, меняются сразу.
import { signal } from '@preact/signals';
import { STRINGS_RU } from './strings.ru.js';

const CACHE_KEY = 'garages.strings.v1';

function readCache(): Record<string, string> {
  try {
    const raw = globalThis.localStorage?.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch { return {}; }
}

/** Перекрытия из конфига (staging меняет тексты без релиза). */
export const overrides = signal<Record<string, string>>(readCache());
/** Ключи, которых нет ни в пакете, ни в перекрытиях, — для отчёта (не падаем, показываем ключ). */
export const missing = new Set<string>();

export function setOverrides(o: unknown): void {
  const clean: Record<string, string> = {};
  if (o && typeof o === 'object') for (const [k, v] of Object.entries(o)) if (typeof v === 'string' && v.length <= 2000) clean[k] = v;
  overrides.value = clean;
  try { globalThis.localStorage?.setItem(CACHE_KEY, JSON.stringify(clean)); } catch { /* нет места */ }
}

function pluralForm(n: number): 'one' | 'few' | 'many' {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return 'many';
  if (b === 1) return 'one';
  if (b >= 2 && b <= 4) return 'few';
  return 'many';
}

/** Разбор `{n, plural, one {…} …}` с учётом вложенных скобок. */
function formatPlural(body: string, n: number): string {
  const forms: Record<string, string> = {};
  let i = 0;
  while (i < body.length) {
    const m = /\s*([a-z=0-9]+)\s*\{/y;
    m.lastIndex = i;
    const r = m.exec(body);
    if (!r) break;
    let depth = 1;
    let j = m.lastIndex;
    for (; j < body.length && depth; j++) { if (body[j] === '{') depth++; else if (body[j] === '}') depth--; }
    forms[r[1]!] = body.slice(m.lastIndex, j - 1);
    i = j;
  }
  const f = forms[`=${n}`] ?? forms[pluralForm(n)] ?? forms['other'] ?? '';
  return f.replace(/#/g, String(n));
}

export function format(msg: string, params: Record<string, unknown> = {}): string {
  let out = '';
  for (let i = 0; i < msg.length; i++) {
    if (msg[i] !== '{') { out += msg[i]; continue; }
    let depth = 1;
    let j = i + 1;
    for (; j < msg.length && depth; j++) { if (msg[j] === '{') depth++; else if (msg[j] === '}') depth--; }
    const inner = msg.slice(i + 1, j - 1);
    const pm = /^\s*(\w+)\s*,\s*plural\s*,(.*)$/s.exec(inner);
    if (pm) out += formatPlural(pm[2]!, Number(params[pm[1]!] ?? 0));
    else out += params[inner.trim()] !== undefined ? String(params[inner.trim()]) : `{${inner}}`;
    i = j - 1;
  }
  return out;
}

export function t(key: string, params?: Record<string, unknown>): string {
  const msg = overrides.value[key] ?? (STRINGS_RU as Record<string, string>)[key];
  if (msg === undefined) { missing.add(key); return key; }
  return params ? format(msg, params) : msg;
}

export function hasKey(key: string): boolean {
  return key in overrides.value || key in STRINGS_RU;
}
